import { createSelector } from "@reduxjs/toolkit";
import { mapKeys, merge, reduce } from "lodash-es";

import { buildSingleItem } from "shared/utils";
import { IVenue, IEditorInvite } from "Venues/types";
import { IJSONAPIItem, IDataKeys } from "shared/types";
import { API_ENDPOINTS } from "shared/constants";

import { dataSelector, buildData, dataMetaSelectorHelper } from "./helpers";

export const venueMetaSelector = (id: string) =>
  dataMetaSelectorHelper(API_ENDPOINTS.VENUE(id));

const venuesSelector = createSelector(
  dataSelector,
  (data) => buildData(data as any, IDataKeys.venue) as IVenue[]
);

const userVenueIdSelector = createSelector(
  dataSelector,
  (data) => buildData(data as any, IDataKeys.userVenues) as IJSONAPIItem[]
);

const searchVenueIdSelector = createSelector(
  dataSelector,
  (data) => buildData(data as any, IDataKeys.searchVenues) as IJSONAPIItem[]
);

const mapIdsToVenues = (venueIds: IJSONAPIItem[], venues: IVenue[]) => {
  const venueMap = mapKeys(venues, ({ id }) => id);
  return reduce(
    venueIds,
    (userVenues: IVenue[], { id, ...venueProps }) => {
      const venue = merge({}, { id }, venueProps, venueMap?.[id]);

      if (venue) {
        return [...userVenues, venue];
      }
      return userVenues;
    },
    []
  );
};

export const userVenueSelector = createSelector(
  [userVenueIdSelector, venuesSelector],
  (userVenueIds, venues) => mapIdsToVenues(userVenueIds, venues)
);

export const searchVenueSelector = createSelector(
  [searchVenueIdSelector, venuesSelector],
  (searchVenueIds, venues) => mapIdsToVenues(searchVenueIds, venues)
);

const dataVenueSelector = createSelector(dataSelector, (data) => data.venue);
export const venueSelectorCreator = (id: string) =>
  createSelector(dataVenueSelector, (venues) => {
    const venue = venues?.[API_ENDPOINTS.VENUE(id)];
    if (venue) {
      return buildSingleItem(venue) as IVenue;
    }
    return null;
  });

const venueEditorInviteSelector = createSelector(
  dataSelector,
  (data) => data.venueInvite
);
export const venueEditorInviteSelectorCreator = (id: string) =>
  createSelector(venueEditorInviteSelector, (editorInvites) => {
    const invite = editorInvites?.[API_ENDPOINTS.VENUE_INVITE(id)];

    if (invite) {
      return buildSingleItem(invite) as IEditorInvite;
    }
    return null;
  });
